/**
 * Legacy bridge — cross-cutting / left-menu domain.
 *
 * Owns access to globally-used EJS-era helpers: `userFactory`,
 * `graphFactory`, `messageFactory`, `myEventEmitter`, `posthog`, `hj`,
 * `graphShareConstructor`, plus `isGraphOnlyMode()` and the share-link
 * helpers. Imported across most React components.
 *
 * Sibling: `views/react/components/ProjectNotes/legacyBridge.js` —
 * Project-Notes-specific `window.*` rewires (`addCommentToProjectNotes`,
 * `getGraphInfoToggle`). Kept separate to keep the notes domain
 * self-contained; the two files are ~not~ duplicates of each other.
 *
 * The `emitEvent`/`onEvent` pair appears in both files for now —
 * candidate for extraction to `views/react/utils/legacyEventBus.js`
 * once the LeftMenu / Notes migrations are stable.
 */

// ─── User-context inputs (for gating + filenames) ────────────────────

export function getCurrentContext() {
    if (typeof userFactory === 'undefined' || !userFactory.getCurrentContext) {
        return null
    }
    return userFactory.getCurrentContext() || null
}

export function getCurrentUser() {
    if (typeof userFactory === 'undefined' || !userFactory.getCurrentUser) {
        return null
    }
    return userFactory.getCurrentUser() || null
}

/**
 * Read a `userFactory` view option (e.g. `hide_max`). Returns null
 * when userFactory hasn't loaded yet.
 */
export function getViewOption(name) {
    if (typeof userFactory === 'undefined' || !userFactory.getViewOption) {
        return null
    }
    return userFactory.getViewOption(name)
}

/**
 * "Graph only" mode — the hard embed flag. When true, the React host
 * suppresses every panel and toolbar so only the graph canvas remains.
 *
 * Triggered exclusively by `?hide_max=1` on the share/embed URL (the
 * "hide statements & AI panel" Share-dialog checkbox).
 *
 * Contrast with `isHideAlways()` (soft): that just boots the panels
 * closed but keeps their toggle buttons available.
 *
 * Iframe context is **not** an automatic trigger. The graph itself is
 * a React component embedded into the host page, not its own iframe, so
 * an embed code wrapping the InfraNodus page in `<iframe>` still loads
 * the full React host — and that host should honor whichever flag the
 * sharer picked (`hide_always`, `hide_edit`, `hide_ai`, or `hide_max`)
 * rather than silently upgrading every iframe to the strictest mode.
 *
 * NOTE: `userFactory.getViewOption('hide_max')` is misleadingly named —
 * it's the "basic mode" toggle (defaults to '1' for most users), NOT
 * the graph-only signal. Don't read it for this purpose.
 */
export function isGraphOnlyMode() {
    if (typeof window === 'undefined') return false
    try {
        const params = new URLSearchParams(window.location.search)
        return params.get('hide_max') === '1'
    } catch (e) {
        return false
    }
}

/**
 * True when the whole InfraNodus page is running inside an `<iframe>` (i.e.
 * a third party embedded `infranodus.com/<user>/<context>` in their site).
 *
 * Comparing `window.self` to `window.top` is allowed even cross-origin —
 * only *reading into* the other window is blocked — and it catches frames
 * at any nesting depth. If the access itself throws (some sandboxed
 * contexts), we're definitely framed, so default to `true`.
 *
 * Purely environmental: it does NOT imply the sharer wanted a stripped-down
 * view. Chrome *visibility* stays driven by the explicit `hide_*` flags;
 * this only switches the surviving chrome to the compact (mobile-style)
 * layout — see the `isMobile` computation in `PanelToggles` and
 * `StatementFilterPanel`.
 */
export function isInIframe() {
    if (typeof window === 'undefined') return false
    try {
        return window.self !== window.top
    } catch (e) {
        return true
    }
}

/**
 * The `?hide_always=1` URL param is the legacy share/embed flag that means
 * "render the bare minimum — graph only, no surrounding panels". It's
 * emitted by `generateIFrameURL` in `views/components/entries/LeftMenuLogic.ejs`
 * for every shared iframe. The React app translates it to:
 *   - StatementFilterPanel hidden
 *   - AI chat panel forced closed
 *   - Graph package mounted in collapsed/iframe layout
 */
export function isHideAlways() {
    if (typeof window === 'undefined') return false
    try {
        const params = new URLSearchParams(window.location.search)
        return params.get('hide_always') === '1'
    } catch (e) {
        return false
    }
}

/**
 * `?hide_edit=1` — soft-close just the **statements list** (the
 * StatementEditor that opens out of `#statementmenu`). The Statements
 * *control* panel (the chip/filter row + "Context" pill) stays
 * visible, and the AI panel is left to its own initial state.
 *
 * Emitted by `generateShareURL` when the sharer had the AI panel open
 * but the statements list closed at share time, so the recipient lands
 * on the same arrangement.
 */
export function isHideEdit() {
    if (typeof window === 'undefined') return false
    try {
        const params = new URLSearchParams(window.location.search)
        return params.get('hide_edit') === '1'
    } catch (e) {
        return false
    }
}

/**
 * `?hide_ai=1` — soft-close the AI Insights panel only. The statements
 * list and its control panel are left to their own initial state.
 *
 * Emitted by `generateShareURL` when the sharer had the statements list
 * open but the AI panel closed at share time.
 */
export function isHideAi() {
    if (typeof window === 'undefined') return false
    try {
        const params = new URLSearchParams(window.location.search)
        return params.get('hide_ai') === '1'
    } catch (e) {
        return false
    }
}

/**
 * `?hide_ai_actions=1` — "Hide AI functionality". The Share dialog's
 * dedicated AI checkbox. Suppresses the entire AI surface for the embed:
 *   - the AI control row *inside* the graph canvas (the iframe-only
 *     "Context → AI" chip and the "AI:" split button),
 *   - the "AI Insights" pill button in `PanelToggles`,
 *   - the `<AIChat>` side panel itself (not rendered in `home/index.jsx`).
 *
 * Independent of `hide_statement_controls` / `hide_project_notes` — each
 * embed-chrome toggle now stands alone (previously all three were folded
 * into the single `hide_max` graph-only flag).
 *
 * The in-graph row is forwarded to `@infranodus/infranodus-graph` via its
 * `hideAiActions` React prop (see `LazyGraph.jsx`). The graph package also
 * reads the same URL param natively in iframe mode; passing the prop wins.
 */
export function isHideAiActions() {
    if (typeof window === 'undefined') return false
    try {
        const params = new URLSearchParams(window.location.search)
        return params.get('hide_ai_actions') === '1'
    } catch (e) {
        return false
    }
}

/**
 * `?hide_statement_controls=1` — "Hide statement controls". The Share
 * dialog checkbox that drops the statement control row at the top of the
 * graph (`<StatementFilterPanel>` / `#categorySwitch`): the "Context" pill
 * + filter chips. Render-gated out in `home/index.jsx`.
 *
 * Independent of the other embed-chrome toggles and of the soft panel
 * open-state flags (`hide_edit` / `hide_ai` / `hide_always`), which govern
 * whether panels *boot* open — this one removes the control row entirely.
 */
export function isHideStatements() {
    if (typeof window === 'undefined') return false
    try {
        const params = new URLSearchParams(window.location.search)
        return params.get('hide_statement_controls') === '1'
    } catch (e) {
        return false
    }
}

/**
 * `?hide_project_notes=1` — "Hide Project notes". The Share dialog checkbox
 * that drops the "Project Notes" pill button in `PanelToggles`, so embed
 * recipients can't open the notes panel. Independent of the other two
 * embed-chrome toggles.
 */
export function isHideProjectNotes() {
    if (typeof window === 'undefined') return false
    try {
        const params = new URLSearchParams(window.location.search)
        return params.get('hide_project_notes') === '1'
    } catch (e) {
        return false
    }
}

/**
 * `?hide_app_bar=1` — drop the header `<AppBar>` (with its Drawer) and
 * nothing else. URL-only: hand-authored for embeds, no Share-dialog
 * checkbox emits it. Fills the gap between the à-la-carte toggles above
 * and graph-only mode (`hide_max=1`), which removes the header but takes
 * every other panel with it — combine this with `hide_statement_controls`,
 * `hide_project_notes`, and `hide_edit` for a chromeless embed that keeps
 * the AI Insights panel (the landing-page hero recipe in
 * `docs/graph/graph-embed-modes.md`).
 */
export function isHideAppBar() {
    if (typeof window === 'undefined') return false
    try {
        const params = new URLSearchParams(window.location.search)
        return params.get('hide_app_bar') === '1'
    } catch (e) {
        return false
    }
}

// ─── Context options (favorite, language, etc.) ──────────────────────

/**
 * Read a per-context option from userFactory. Returns mixed types
 * ("true" string, boolean true, 1, "1") — callers must normalize.
 *
 * Prefer the React store for fields it tracks (e.g. favorite lives at
 * `useContextsStore.contexts[i].isFavorite` and is already a boolean).
 * Use this for fields that aren't yet in the React store.
 */
export function getContextOption(name) {
    if (typeof userFactory === 'undefined' || !userFactory.getContextOptions) {
        return null
    }
    return userFactory.getContextOptions(name)
}

// ─── Project notes flush ─────────────────────────────────────────────
//
// Project notes auto-saves on a debounce. Flows that trigger a destructive
// reload (e.g. saving language settings) need to wait for any pending save
// to actually hit the server, otherwise the user's typed text is lost.
// ProjectNotes registers a Promise-returning flush function here; the
// caller awaits flushProjectNotesPendingSave() before navigating away.

let flushProjectNotesFn = null

/**
 * Register a flush function. Returns an unregister callback so callers
 * can wire it via `useEffect` cleanup.
 */
export function registerProjectNotesFlush(fn) {
    flushProjectNotesFn = fn
    return () => {
        if (flushProjectNotesFn === fn) flushProjectNotesFn = null
    }
}

/**
 * Run the registered flush (if any) and resolve when done. Errors are
 * swallowed: a notes-save failure shouldn't abort the destructive reload
 * the caller is about to perform.
 */
export async function flushProjectNotesPendingSave() {
    if (typeof flushProjectNotesFn !== 'function') return
    try {
        await flushProjectNotesFn()
    } catch (e) {
        console.warn(
            '[legacyBridge] project notes flush failed; continuing reload:',
            e
        )
    }
}

// ─── Event emitter ───────────────────────────────────────────────────

export function emitEvent(eventName, payload) {
    const emitter = typeof window !== 'undefined' ? window.myEventEmitter : null
    if (emitter?.emit) {
        emitter.emit(eventName, payload)
    }
}

export function onEvent(eventName, handler) {
    const emitter = typeof window !== 'undefined' ? window.myEventEmitter : null
    if (!emitter) return () => {}
    emitter.on(eventName, handler)
    return () => emitter.removeListener(eventName, handler)
}

// ─── User messages (top-of-page transient toast) ─────────────────────

export function showMessage(text, duration = 4000) {
    if (typeof window === 'undefined') return
    if (window.messageFactory?.showMessage) {
        window.messageFactory.showMessage(text, duration)
    }
}

// ─── Analytics tracking ──────────────────────────────────────────────

export function trackEvent(eventName, properties) {
    if (typeof window === 'undefined') return
    if (typeof window.hj === 'function') {
        window.hj('event', eventName)
    }
    if (window.posthog?.capture) {
        window.posthog.capture(eventName, properties)
    }
}

// ─── Share dialog (legacy graphShareConstructor) ─────────────────────

/**
 * Return the jQuery-wrapped DOM node built by the legacy share-link
 * constructor (`window.graphShareConstructor.generateShareLink()`). The
 * node is a freshly-built form holding the embed URL, copy fields,
 * "remove all controls" checkbox, and privacy toggle button.
 *
 * Returns null when the constructor isn't ready (early bootstrap).
 * Caller mounts the node and wires post-mount handlers via
 * {@link attachShareDialogHandlers}.
 */
export function generateShareLinkNode() {
    if (typeof window === 'undefined') return null
    const ctor = window.graphShareConstructor
    if (!ctor || typeof ctor.generateShareLink !== 'function') return null
    try {
        return ctor.generateShareLink()
    } catch (e) {
        console.warn('[legacyBridge] generateShareLink failed:', e)
        return null
    }
}

// The three independent embed-chrome toggles. Each maps a checkbox id to
// the userFactory view option that `generateShareURL` reads to emit the
// matching URL param, plus the `previewEmbedHide` part key. Both share-form
// variants (post-public + pre-public) reuse the same checkbox ids, so one
// handler set covers whichever variant is currently mounted.
const EMBED_HIDE_TOGGLES = [
    { id: 'embedHideStatements', option: 'hide_statement_controls', part: 'statements' },
    { id: 'embedHideAi', option: 'hide_ai_actions', part: 'ai' },
    { id: 'embedHideNotes', option: 'hide_project_notes', part: 'notes' },
]

/**
 * Attach interactive handlers to the share form after it has been
 * mounted: the three "hide …" embed toggles, privacy submit, and copy-on-
 * focus behavior on the URL/embed inputs. Idempotent — `.off()` before
 * each `.on()` so handlers don't double-bind across reopens.
 *
 * Each embed toggle (mirrors `LeftMenuLogic.ejs:shareGraphDataToggle`):
 *   - Persists its dedicated view option so `generateShareURL` emits the
 *     matching `?hide_…=1` param next time and the form re-renders
 *     pre-checked. These options are embed-only — unlike the legacy
 *     `hide_max`, none of them touch basic/advanced mode.
 *   - Applies a visual-only preview class to <body> via
 *     `window.previewEmbedHide(part, checked)` — does NOT close live panels.
 *   - Refreshes the embed URL + iframe code fields.
 *
 * Requires jQuery (`window.$`), which the legacy app loads globally.
 */
export function attachShareDialogHandlers() {
    if (typeof window === 'undefined' || !window.$) return
    const $ = window.$
    const ctor = window.graphShareConstructor
    if (!ctor) return

    const refreshEmbedFields = () => {
        // No-op when the URL/code inputs aren't in the DOM (pre-public
        // variant): `.val()` on an empty jQuery set does nothing.
        const url = ctor.generateShareURL?.() || ''
        $('#embedurl').val(url)
        $('#embedcode').val(ctor.generateIFrameURL?.(url) || '')
    }

    EMBED_HIDE_TOGGLES.forEach(({ id, option, part }) => {
        const $box = $('#' + id)
        $box.off('change.share')
        $box.on('change.share', function () {
            const checked = !!$box.prop('checked')
            if (typeof window.userFactory !== 'undefined') {
                window.userFactory.setViewOption?.(option, checked ? 1 : 0)
            }
            if (typeof window.previewEmbedHide === 'function') {
                window.previewEmbedHide(part, checked)
            }
            refreshEmbedFields()
        })
    })

    $('#privacybutton').off('click')
    $('#privacybutton').on('click', function () {
        emitEvent('onStatementsAction', { action: 'change_privacy_graph' })
        trackEvent('change_privacy_graph')
    })

    // Auto-select on focus so users can copy with Cmd-C immediately.
    $('#embedurl').off('focus.share')
    $('#embedurl').on('focus.share', function () {
        setTimeout(() => $('#embedurl').select(), 0)
    })
    $('#embedcode').off('focus.share')
    $('#embedcode').on('focus.share', function () {
        setTimeout(() => $('#embedcode').select(), 0)
    })

    // Sync each preview class to its checkbox's initial state so that, when
    // the box is pre-checked from a previous session, the visual preview
    // matches the emitted URL immediately on first open.
    if (typeof window.previewEmbedHide === 'function') {
        EMBED_HIDE_TOGGLES.forEach(({ id, part }) => {
            const $box = $('#' + id)
            window.previewEmbedHide(part, !!($box.length && $box.prop('checked')))
        })
    }
}

/**
 * Clear all embed preview tints when the Share dialog closes — leaves no
 * visual residue on the page even if the user toggled a checkbox before
 * dismissing. The persisted view options are intentionally untouched so
 * the next dialog open restores the checkbox state.
 */
export function detachShareDialogHandlers() {
    if (typeof window === 'undefined') return
    if (typeof window.clearEmbedPreview === 'function') {
        window.clearEmbedPreview()
    }
}
